<template>
  <v-dialog :value="active" width="325" :dark="dark" @click:outside="close">
    <v-card :dark="dark">
      <v-card-title>Select a puzzle!</v-card-title>
      <v-card-subtitle v-if="$ark.active || !user" class="pb-1">
        {{ wins.length }} / {{ todayOrdinal }} puzzles completed!
      </v-card-subtitle>
      <v-card-subtitle
        v-else
        class="pt-1 pb-1"
        :style="{
          color: dark ? 'white' : 'black !important',
        }"
      >
        <strong>
          {{ wins.length }} / {{ todayOrdinal }}
          Blockables
        </strong>
        completed!
        <br />
        {{ completionBreakdown }}
      </v-card-subtitle>
      <v-card-text>
        <v-row justify="center">
          <v-col cols="12" class="pb-0">
            <v-date-picker
              ref="datepicker"
              v-model="picker"
              style="overflow-y: hidden"
              :color="$colors.blue"
              :dark="dark"
              full-width
              :min="debutDate"
              :max="today"
              @click:month="updateMonth($event)"
            />
          </v-col>
        </v-row>
      </v-card-text>
      <v-card-actions class="py-0">
        <v-col cols="12" class="d-flex justify-center pt-0">
          <v-btn
            block
            :color="$colors.blue"
            :dark="dark"
            :disabled="!picker"
            @click="play(picker)"
          >
            Play {{ humanReadable(picker) }}
          </v-btn>
        </v-col>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { mdiCheck, mdiChevronLeft, mdiChevronRight } from "@mdi/js";

export default {
  props: { pppActive: Boolean },
  data: () => ({
    active: false,
    allPuzzles: [],
    icons: {
      check: mdiCheck,
      chevronLeft: mdiChevronLeft,
      chevronRight: mdiChevronRight,
    },
    loading: false,
    offset: 0,
    picker: null,
    wins: [],
  }),
  computed: {
    completionBreakdown() {
      let counts = {
        crown: 0,
        sax: 0,
        tada: 0,
      };
      if (this.$ark.active) return counts;
      this.$store.state.USER.stats.forEach(s => {
        counts[s.symbol]++;
      });
      let cb = "";
      if (counts.crown) {
        cb += `${counts["crown"]} 👑 | `;
      }
      if (counts.sax) {
        cb += `${counts["sax"]} 🎷 | `;
      }
      if (counts.tada) {
        cb += `${counts["tada"]} 🎉`;
      }
      if ((counts.crown || counts.sax) && !counts.tada) {
        cb = cb.slice(0, -2);
      }
      return cb;
    },
    currentMonth() {
      return parseInt(this.today.slice(5, 7));
    },
    currentYear() {
      return parseInt(this.today.slice(0, 4));
    },
    dark() {
      return this.$store.state.USER.settings.dark;
    },
    debutDate() {
      return process.env.VUE_APP_DEBUT_DATE;
    },
    emojiMap() {
      return {
        crown: "👑",
        sax: "🎷",
        tada: "🎉",
      };
    },
    puzzleNumber() {
      return this.$store.state.PUZZLE.metadata.puzzleNumber;
    },
    stats() {
      return this.$store.state.USER.stats;
    },
    today() {
      return this.$store.getters["USER/TODAY"](
        process.env.VUE_APP_DEPLOYMENT_PLATFORM
      );
    },
    todayOrdinal() {
      return this.getOrdinal(this.today);
    },
    user() {
      return !this.$ark.active && this.$store.state.sb_user;
    },
    winByGrid() {
      return this.$store.getters["PUZZLE/WIN_BY_GRID"];
    },
  },
  watch: {
    async active() {
      if (!this.picker) {
        this.picker = this.today;
      }
      // this.picker = null;
      if (this.active) {
        await this.getWins();
        await this.$nextTick();
        let targetElement =
          this.$refs.datepicker.$el.querySelector(".v-picker__body");

        if (targetElement) {
          this.observeElement(targetElement);
        }

        await this.pullPuzzles();
        await this.markCompletes();
      } else {
        if (this.observer) {
          this.observer.disconnect();
        }
      }
    },
    async offset() {
      await this.pullPuzzles();
      await this.markCompletes();
    },
    async pppActive() {
      if (this.pppActive) {
        this.active = true;
      }
    },
  },
  beforeDestroy() {
    // Disconnect the observer when the component is destroyed to prevent memory leaks
    if (this.observer) {
      this.observer.disconnect();
    }
  },
  methods: {
    observeElement(element) {
      // Create a MutationObserver instance
      this.observer = new MutationObserver(mutations => {
        mutations.forEach(mutation => {
          let ps = mutation.previousSibling;
          let target = mutation.target;
          if (!ps) return;
          if (!target.classList.contains("v-date-picker-header__value")) return;
          if (ps.classList.contains("tab-reverse-transition-leave")) {
            this.offset -= 1;
          } else if (ps.classList.contains("tab-transition-leave")) {
            this.offset += 1;
          }
        });
      });

      // Specify what to observe: attributes, children, or subtree
      let config = {
        attributes: false, // Observe attribute changes
        childList: true, // Observe direct children
        subtree: true, // Observe all descendants
      };

      // Start observing the target element with the configured options
      this.observer.observe(element, config);
    },
    close() {
      this.active = false;
      this.allPuzzles = [];
      this.$emit("modalClose");
    },
    decrementOffset() {
      this.offset -= 1;
    },
    getDaysInMonth(month, year) {
      var date = new Date(year, month, 1);
      var days = [];
      while (date.getMonth() === month) {
        days.push(new Date(date).toISOString().split("T")[0]);
        date.setDate(date.getDate() + 1);
      }
      return days;
    },
    getFullDate(d) {
      return new Date(d).toUTCString().substring(0, 16);
    },
    getMonthAgo(months, currentMonth, offset) {
      // Get the index of the current month
      let currentIndex = currentMonth - 1;

      // Calculate the new index considering the offset
      // Add months.length to ensure positive index when going backward
      let newIndex = (currentIndex + offset + months.length) % months.length;

      // Return the month at the new index
      return months[newIndex];
    },
    getOrdinal(d) {
      let o = null;
      try {
        let then = this.debutDate.split("-");
        then = new Date(then[0], then[1] - 1, then[2]);
        let now = d.split("-");
        now = new Date(now[0], now[1] - 1, now[2]);
        o = Math.round((now - then) / (1000 * 60 * 60 * 24)) + 1;
      } catch {
        o = null;
      }
      return o;
    },
    async getWins() {
      try {
        if (this.user) {
          let wonPuzzles = this.stats.map(s => s.ordinal);
          this.wins = [...wonPuzzles];
          return;
        }
        let wins = await this.$store.dispatch(
          "LOCAL_STORAGE_GET",
          "blockables-ark-wins"
        );
        if (!wins) {
          wins = [];
        } else {
          wins = JSON.parse(wins);
          if (!wins.length) {
            wins = [];
          }
        }
        this.wins = [...wins];
      } catch {
        this.wins = [];
      }
    },
    getYearAndMonth(offset) {
      // Adjust the month by subtracting the offset
      let targetMonth = this.currentMonth - offset;

      // Calculate the number of years to subtract
      let yearAdjustment = Math.floor((targetMonth - 1) / 12);

      // Calculate the correct year
      let targetYear = this.currentYear + yearAdjustment;

      // Adjust the month to be within 1 to 12
      targetMonth = ((targetMonth - 1) % 12) + 1;
      if (targetMonth <= 0) {
        targetMonth += 12;
      }

      return {
        year: targetYear,
        month: targetMonth,
      };
    },
    humanReadable(d) {
      if (!d) {
        d = new Date(
          new Date(this.today).toLocaleString("en-US", {
            timeZone: "GMT",
          })
        );
      } else {
        d = new Date(
          new Date(d).toLocaleString("en-US", {
            timeZone: "GMT",
          })
        );
      }

      let dd = d.getDate();

      const month = [
        "Jan",
        "Feb",
        "Mar",
        "Apr",
        "May",
        "Jun",
        "Jul",
        "Aug",
        "Sep",
        "Oct",
        "Nov",
        "Dec",
      ][d.getMonth()];

      return `${month} ${dd}${this.nth(dd)}, ${d.getFullYear()}`;
    },
    incrementOffset() {
      this.offset += 1;
    },
    async markCompletes() {
      await this.$nextTick();
      let months = [
        "January",
        "February",
        "March",
        "April",
        "May",
        "June",
        "July",
        "August",
        "September",
        "October",
        "November",
        "December",
      ];
      let monthSelection = this.$refs.datepicker.$el.querySelector(
        ".v-date-picker-header__value"
      );
      monthSelection = monthSelection.innerText.split(" ");
      let calendarMonth = monthSelection[0];
      let calendarYear = monthSelection[1];

      // if direct-to-month
      if (monthSelection.length == 1) {
        calendarMonth = this.getMonthAgo(
          months,
          this.currentMonth,
          this.offset
        );
        calendarYear = monthSelection[0].trim();
      }
      if (monthSelection.length == 2) {
        calendarMonth = monthSelection[0].trim();
        calendarYear = monthSelection[1].trim();
      }
      // if switching months
      if (monthSelection.length > 2) {
        calendarMonth = monthSelection[1].split("\n")[1].trim();
        calendarYear = monthSelection[2].trim();
      }
      let monthIndex = months.indexOf(calendarMonth) + 1;

      let cal = Array.from(
        this.$refs.datepicker.$el.querySelectorAll(".v-date-picker-table tr")
      );

      let lastSmtwtfsIndex = 0;
      for (let i = 0; i < cal.length; i++) {
        if (cal[i].textContent == "SMTWTFS") {
          lastSmtwtfsIndex = i;
        }
      }
      cal = cal.slice(lastSmtwtfsIndex + 1);

      cal.forEach(tr => {
        Array.from(tr.children).forEach(td => {
          if (!td.textContent) return;
          td.id = `${calendarYear}-${
            monthIndex < 10 ? "0" + monthIndex : monthIndex
          }-${
            parseInt(td.textContent) < 10
              ? "0" + td.textContent
              : td.textContent
          }`;
          let validPuzzle = this.allPuzzles.find(p => p.date == td.id);
          if (typeof validPuzzle === "undefined") return;
          if (this.$ark.active || !this.user) {
            td.setAttribute("ordinal", validPuzzle.ordinal);
            if (!this.wins.includes(validPuzzle.ordinal)) return;
            td.querySelector("div").innerHTML =
              '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" role="img" aria-hidden="true" class="v-icon__svg"><path d="M21,7L9,19L3.5,13.5L4.91,12.09L9,16.17L19.59,5.59L21,7Z"></path></svg>';
            td.querySelector("button").classList.add("completed");
          } else {
            if (validPuzzle.symbol) {
              td.querySelector("button").classList.add("completed");
              let div = td.querySelector("div");
              div.innerText = this.emojiMap[validPuzzle.symbol];
              div.style.fontSize = "18px";
              div.style.marginBottom = "4px";
            }
          }
        });
      });
    },
    nth(d) {
      if (d > 3 && d < 21) return "th";
      switch (d % 10) {
        case 1:
          return "st";
        case 2:
          return "nd";
        case 3:
          return "rd";
        default:
          return "th";
      }
    },
    async play(d) {
      this.$analytics?.logEvent("play_past_puzzle");
      await this.$store.dispatch("USER/REINITIALIZE");
      this.$store.commit("USER/INCREMENT_PLAY_AGAIN_COUNTER");
      this.$store.commit("USER/WIN_DIALOG_SET", false);
      await this.$store.dispatch("USER/TIMER_START");
      this.$store.commit("USER/EXAMPLE_SET", false);
      this.$store.commit("USER/GAME_START", false);
      this.$router.push("/puzzles/" + d);
      this.close();
      this.triggerMidroll();
    },
    async pullPuzzles() {
      let puzzleData = null;
      let yearAndMonth = this.getYearAndMonth(-this.offset);
      let year = yearAndMonth["year"];
      let month = yearAndMonth["month"];

      if (this.$ark.active) {
        puzzleData = await this.$fetch(
          "rpc/get_arkadium_puzzles_for_month",
          "POST",
          {
            month: month,
            year: year,
          }
        );
      } else if (!this.user) {
        const { data, error } = await this.$db.rpc(
          "get_main_site_puzzles_for_month",
          {
            month: month,
            year: year,
          }
        );
        if (!error) {
          puzzleData = data;
        }
      } else {
        puzzleData = [];
        this.stats.forEach(s => {
          let sameYear = parseInt(s.date.substring(0, 4)) == parseInt(year);
          let sameMonth = parseInt(s.date.substring(5, 7)) == parseInt(month);
          if (sameYear && sameMonth) {
            puzzleData.push({
              date: s.date,
              ordinal: s.ordinal,
              symbol: s.symbol,
            });
          }
        });
      }

      this.allPuzzles.push(...puzzleData);
    },
    async updateMonth(e) {
      let [selectedYear, selectedMonth] = e.split("-").map(Number);
      let selectedMonths = selectedYear * 12 + selectedMonth;
      let currentMonths = this.currentYear * 12 + this.currentMonth;
      this.offset = selectedMonths - currentMonths;
    },
    async triggerMidroll() {
      if (this.won()) {
        try {
          let wins = await this.$store.dispatch(
            "LOCAL_STORAGE_GET",
            "blockables-ark-wins"
          );
          if (!wins.length) {
            wins = [this.puzzleNumber];
          } else {
            wins = JSON.parse(wins);
            wins.push(this.puzzleNumber);
          }
          await this.$store.dispatch("LOCAL_STORAGE_SET", {
            id: "blockables-ark-wins",
            value: JSON.stringify(wins),
          });
          await this.$store.dispatch("USER/ELIMINATE_LOCAL_STORAGE");
        } catch {
          // localstorage inaccessible, ignore it
        }
        // this.navToHome();
        if (
          !this.$ark.active &&
          process.env.VUE_APP_DEPLOYMENT_PLATFORM == "arkadium"
        ) {
          this.$ark.api.handleMidrollRequest();
        }
      }
    },
    won() {
      return this.winByGrid;
    },
  },
};
</script>

<style>
.completed {
  color: var(--blue) !important;
  pointer-events: none;
  cursor: default;
}

.v-card {
  display: flex !important;
  flex-direction: column;
}

.v-card__text {
  flex-grow: 1;
  overflow: auto;
}

.v-date-picker-title__date {
  font-size: 16px;
}

.v-picker__title {
  padding: 12px 16px;
  display: none;
}

.v-picker__body {
  flex: 0.15 0 auto;
}

.v-date-picker-title__year {
  display: none;
}
</style>
